import { Badge } from "@foundation/components/ui/badge";
import { Checkbox } from "@foundation/components/ui/checkbox";
import { Loader2, Sparkles } from "lucide-react";
import type { AiSkill } from "../../types/boostPolicy";

type AiSkillsSelectorProps = {
  skills: AiSkill[];
  selectedIds: string[];
  isLoading: boolean;
  onChange: (selectedIds: string[]) => void;
};

export const AiSkillsSelector = ({
  skills,
  selectedIds,
  isLoading,
  onChange,
}: AiSkillsSelectorProps) => {
  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((s) => s !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading AI skills...
      </div>
    );
  }

  if (skills.length === 0) {
    return (
      <p className="text-sm text-muted-foreground italic py-2">
        No AI skills available.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {skills.map((skill) => (
        <label
          key={skill.id}
          className="flex items-start gap-3 p-3 rounded-lg border border-border bg-card cursor-pointer hover:bg-accent/50 transition-colors"
        >
          <Checkbox
            checked={selectedIds.includes(skill.id)}
            onCheckedChange={() => toggle(skill.id)}
            className="mt-0.5 shrink-0"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-foreground">{skill.skill_name}</span>
              {skill.default_flag && (
                <Badge variant="secondary" className="text-xs px-1.5 py-0">
                  Default
                </Badge>
              )}
              {skill.tenant_id === null && (
                <Badge variant="outline" className="text-xs px-1.5 py-0 text-muted-foreground">
                  Global
                </Badge>
              )}
            </div>
          </div>
        </label>
      ))}

      {selectedIds.length > 0 && (
        <p className="text-xs text-muted-foreground flex items-center gap-1 pt-1">
          <Sparkles className="h-3 w-3" />
          {selectedIds.length} skill{selectedIds.length > 1 ? "s" : ""} will be applied during generation
        </p>
      )}
    </div>
  );
};
